// lib/grafik/analiza.ts — ANALIZA ROZKŁADU GRAFIKU (Restauracje → Grafik, R5-15, 2026-10-04).
//
// Czyste funkcje: liczniki szkoła × dzień, rozpiętość tygodnia, sąsiednie dni restauracji, sumy miasta
// i stabilne kolory restauracji. Bez Reacta i bez sieci — ekran liczy z PEŁNEGO zbioru par (backend
// nie stronicuje grafiku), więc licznik nigdy nie pochodzi z wycinka danych.
//
// To jest WSPARCIE DECYZJI, nie reguła biznesowa: ostrzeżenia niczego nie blokują i niczego nie
// przestawiają. Reguła grafiku pozostaje regułą systemu głównego: zaznaczony dzień pary
// (`SchoolRestaurantAvailability.IsConfirmed = 1`) = restauracja dostępna w tej szkole tego dnia.

/** Dni w konwencji systemu głównego (`TsDayOfWeek`): 0 = poniedziałek … 6 = niedziela. */
export const DNI_TYGODNIA = [0, 1, 2, 3, 4, 5, 6] as const;
/** Priorytet operacyjny: poniedziałek–piątek. */
export const DNI_ROBOCZE = [0, 1, 2, 3, 4] as const;
export const SKROT_DNIA = ['PON', 'WT', 'ŚR', 'CZW', 'PT', 'SOB', 'ND'] as const;
export const NAZWA_DNIA = ['poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota', 'niedziela'] as const;

/**
 * Próg ostrzeżenia o nierównym rozkładzie: rozpiętość (max − min) liczby restauracji w dniach
 * pon–pt. Przy 3 i więcej różnica między najsłabszym a najmocniejszym dniem przestaje być
 * „szumem” jednej restauracji. Przykład OWNERA (2–7) daje 5.
 */
export const PROG_NIEROWNEGO_ROZKLADU = 3;

export interface ParaGrafiku {
  szkolaId: number;
  restauracjaId: number;
  /** Siedem dni 0 = pon … 6 = nd; `true` = zaznaczone w systemie głównym. */
  dni: readonly boolean[];
}

/** Ile restauracji ma szkoła w każdym dniu tygodnia (0–6). Zero jest jawnym zerem, nie brakiem. */
export function licznikiSzkoly(pary: readonly ParaGrafiku[], szkolaId: number): number[] {
  const liczniki = [0, 0, 0, 0, 0, 0, 0];
  for (const p of pary) {
    if (p.szkolaId !== szkolaId) continue;
    p.dni.forEach((zaznaczony, dzien) => {
      if (zaznaczony && dzien >= 0 && dzien < 7) liczniki[dzien] = (liczniki[dzien] ?? 0) + 1;
    });
  }
  return liczniki;
}

export interface OcenaRozkladu {
  min: number;
  max: number;
  srednia: number;
  rozpietosc: number;
  nierowny: boolean;
}

/** Ocena rozkładu pon–pt jednej szkoły (liczby restauracji w dniach roboczych). */
export function ocenRozklad(licznikiPonPt: readonly number[]): OcenaRozkladu {
  const liczby = licznikiPonPt.length ? [...licznikiPonPt] : [0];
  const min = Math.min(...liczby);
  const max = Math.max(...liczby);
  const srednia = Math.round((liczby.reduce((a, b) => a + b, 0) / liczby.length) * 10) / 10;
  const rozpietosc = max - min;
  return { min, max, srednia, rozpietosc, nierowny: rozpietosc >= PROG_NIEROWNEGO_ROZKLADU };
}

/** Dni robocze (0–4), w które para jest zaznaczona, rosnąco. */
export function dniRoboczePary(dni: readonly boolean[]): number[] {
  return DNI_ROBOCZE.filter((d) => dni[d] === true);
}

/**
 * OSTRZEŻENIE O SĄSIEDNICH DNIACH (CONSECUTIVE_DAY_WARNING).
 *
 * Gdy restauracja jest w szkole 2–3 razy w tygodniu, zwraca pary sąsiednich dni (pon+wt, wt+śr,
 * śr+czw, czw+pt) — np. WT+ŚR zamiast WT+CZW. Pusta lista = brak ostrzeżenia.
 *
 * Dlaczego TYLKO 2–3 dni: przy 4 i 5 dniach w tygodniu pięciodniowym sąsiedztwo jest NIEUNIKNIONE
 * (nie istnieje układ bez sąsiadów), więc ostrzeżenie nie wskazywałoby niczego do poprawienia,
 * a zasypałoby ekran (w arkuszu OWNERA 75 par ma 4–5 dni). Przy 3 dniach istnieje układ bez
 * sąsiadów (pon/śr/pt), przy 2 — każdy układ z przerwą.
 */
export function sasiednieDni(dni: readonly boolean[]): Array<[number, number]> {
  const robocze = dniRoboczePary(dni);
  if (robocze.length < 2 || robocze.length > 3) return [];
  const pary: Array<[number, number]> = [];
  for (let i = 1; i < robocze.length; i++) {
    const a = robocze[i - 1] as number;
    const b = robocze[i] as number;
    if (b - a === 1) pary.push([a, b]);
  }
  return pary;
}

/** Opis ostrzeżenia, np. „WT + ŚR”. */
export function opisSasiednichDni(pary: ReadonlyArray<readonly [number, number]>): string {
  return pary.map(([a, b]) => `${SKROT_DNIA[a]} + ${SKROT_DNIA[b]}`).join(', ');
}

export interface AgregatMiasta {
  szkol: number;
  restauracji: number;
  /** Suma przypisań (zaznaczonych dni) w dniach 0–6 po wszystkich szkołach miasta. */
  przypisaniaWDniu: number[];
}

/** Sumy miasta = DOKŁADNA suma liczników jego szkół (A8). */
export function agregatMiasta(pary: readonly ParaGrafiku[], szkolyMiasta: readonly number[]): AgregatMiasta {
  const zbiorSzkol = new Set(szkolyMiasta);
  const restauracje = new Set<number>();
  const przypisaniaWDniu = [0, 0, 0, 0, 0, 0, 0];
  for (const p of pary) {
    if (!zbiorSzkol.has(p.szkolaId)) continue;
    let ktorykolwiek = false;
    p.dni.forEach((z, d) => {
      if (z && d >= 0 && d < 7) {
        przypisaniaWDniu[d] = (przypisaniaWDniu[d] ?? 0) + 1;
        ktorykolwiek = true;
      }
    });
    if (ktorykolwiek) restauracje.add(p.restauracjaId);
  }
  return { szkol: zbiorSzkol.size, restauracji: restauracje.size, przypisaniaWDniu };
}

/** Czy w zbiorze par są zaznaczenia weekendowe — weekend pokazujemy tylko wtedy (pon–pt to priorytet). */
export function maWeekend(pary: readonly ParaGrafiku[]): boolean {
  return pary.some((p) => p.dni[5] === true || p.dni[6] === true);
}

// ── KOLORY RESTAURACJI — prezentacja, NIGDY tożsamość ─────────────────────────────────────────
//
// Tożsamością restauracji jest jej ID z systemu głównego. Kolor jest wyłącznie pomocą wzrokową:
//  • STABILNY — ta sama restauracja ma ten sam kolor we wszystkich dniach, szkołach i miastach;
//    zależy tylko od zbioru restauracji, nie od kolejności renderu;
//  • BEZ KOLIZJI w obrębie miasta (do rozmiaru palety) — kolorowanie zachłanne po rosnącym ID:
//    restauracja dostaje pierwszy kolor niezajęty przez restauracje z tych samych miast. Nowe
//    restauracje mają wyższe ID, więc dopisanie nowej nie przestawia kolorów istniejących;
//  • nazwa i skrót zawsze są obok koloru (dostępność — kolor nie jest jedynym nośnikiem).

/** Paleta inspirowana arkuszem OWNERA (zieleń, żółcie, błękity, rdza, róż, śliwka…), rozszerzona. */
export const PALETA_RESTAURACJI = [
  // Pierwsza dwunastka — najbardziej odróżnialne barwy (miasto ma zwykle do ~15 restauracji);
  // dwa żółcie i dwa błękity arkusza OWNERA nie stoją już obok siebie w kolejności przydziału.
  '#00a933', '#ffde59', '#729fcf', '#be480a', '#a1467e', '#e8a202', '#2a9d8f', '#ffa6a6',
  '#6b5e9b', '#81d41a', '#b2b2b2', '#e76f51', '#b4c7dc', '#ffff6d', '#8ecae6', '#c77dff',
  '#f4a261', '#90be6d', '#577590', '#f28482', '#43aa8b', '#bc6c25', '#7f5539', '#5e60ce',
] as const;

/** Kolor tekstu na tle chipa — czytelność (próg luminancji WCAG). */
export function kolorTekstu(tlo: string): '#191923' | '#ffffff' {
  const hex = tlo.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r ?? 0) + 0.7152 * lin(g ?? 0) + 0.0722 * lin(b ?? 0);
  return L > 0.4 ? '#191923' : '#ffffff';
}

/**
 * Kolory restauracji. `miastaRestauracji`: restauracja → miasta, w których ma pary.
 * Wynik jest deterministyczny dla danego zbioru wejściowego.
 */
export function koloryRestauracji(miastaRestauracji: ReadonlyMap<number, ReadonlySet<number>>): Map<number, string> {
  const wynik = new Map<number, number>();
  const zajeteWMiescie = new Map<number, Set<number>>();
  const ids = [...miastaRestauracji.keys()].sort((a, b) => a - b);
  for (const id of ids) {
    const miasta = miastaRestauracji.get(id) ?? new Set<number>();
    const zajete = new Set<number>();
    for (const m of miasta) for (const k of zajeteWMiescie.get(m) ?? []) zajete.add(k);
    let indeks = 0;
    while (zajete.has(indeks) && indeks < PALETA_RESTAURACJI.length) indeks++;
    // Paleta wyczerpana w mieście (>24 restauracji): kolejny kolor po ID — skrót i nazwa i tak rozróżniają.
    if (indeks >= PALETA_RESTAURACJI.length) indeks = id % PALETA_RESTAURACJI.length;
    wynik.set(id, indeks);
    for (const m of miasta) {
      if (!zajeteWMiescie.has(m)) zajeteWMiescie.set(m, new Set());
      zajeteWMiescie.get(m)!.add(indeks);
    }
  }
  return new Map([...wynik].map(([id, i]) => [id, PALETA_RESTAURACJI[i] as string]));
}

/**
 * Skrót restauracji do chipa (2–3 znaki z inicjałów), UNIKALNY w obrębie zbioru. Kolizja dostaje
 * dopisek z ID — skrót ma rozróżniać, a nie być ładny.
 */
export function skrotyRestauracji(restauracje: ReadonlyArray<{ id: number; nazwa: string }>): Map<number, string> {
  const baza = (nazwa: string) => {
    const slowa = nazwa
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter(Boolean);
    if (slowa.length === 0) return '?';
    if (slowa.length === 1) return (slowa[0] as string).slice(0, 3);
    return slowa.slice(0, 3).map((s) => s[0]).join('');
  };
  const wynik = new Map<number, string>();
  const uzyte = new Map<string, number>();
  for (const r of [...restauracje].sort((a, b) => a.id - b.id)) {
    let s = baza(r.nazwa).toUpperCase();
    if (uzyte.has(s)) s = `${s}${r.id}`;
    uzyte.set(s, r.id);
    wynik.set(r.id, s);
  }
  return wynik;
}
